import { useCallback, useEffect, useRef } from 'react';
import type { KeyboardEvent, PointerEvent, ReactNode } from 'react';
import { Close } from '@mui/icons-material';
import { Box, Drawer, IconButton, Typography } from '@mui/material';
import { useResizableWidth } from './use-resizable-width';

type Props = {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  titleId: string;
  storageKey: string;
  children: ReactNode;
};

const DEFAULT_WIDTH = 720;
const MIN_WIDTH = 420;
const KEY_STEP = 16;

export const ResizableDrawer = ({ open, onClose, title, titleId, storageKey, children }: Props) => {
  const { width, bounds, setWidth } = useResizableWidth(storageKey, DEFAULT_WIDTH, MIN_WIDTH);

  const widthRef = useRef(width);
  widthRef.current = width;

  const pointerIdRef = useRef<number | null>(null);
  const handleElRef = useRef<HTMLElement | null>(null);
  const prevUserSelectRef = useRef<string | null>(null);

  // Единая точка завершения drag: pointerup/cancel, lostpointercapture, закрытие, размонтирование
  const endDrag = useCallback(() => {
    const pointerId = pointerIdRef.current;
    if (pointerId === null) return;
    pointerIdRef.current = null;
    try {
      handleElRef.current?.releasePointerCapture(pointerId);
    } catch {
      // захват уже снят или элемент удалён
    }
    handleElRef.current = null;
    if (prevUserSelectRef.current !== null) {
      document.body.style.userSelect = prevUserSelectRef.current;
      prevUserSelectRef.current = null;
    }
    setWidth(widthRef.current, true);
  }, [setWidth]);

  useEffect(() => {
    if (!open) endDrag();
  }, [open, endDrag]);

  useEffect(() => endDrag, [endDrag]);

  const onPointerDown = (e: PointerEvent<HTMLElement>) => {
    if (pointerIdRef.current !== null) return;
    e.preventDefault();
    // Drag стартует только если захват удался: без него pointerup за пределами ручки не придёт
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      return;
    }
    pointerIdRef.current = e.pointerId;
    handleElRef.current = e.currentTarget;
    prevUserSelectRef.current = document.body.style.userSelect;
    document.body.style.userSelect = 'none';
  };

  const onPointerMove = (e: PointerEvent<HTMLElement>) => {
    if (pointerIdRef.current !== e.pointerId) return;
    setWidth(window.innerWidth - e.clientX);
  };

  const onPointerEnd = (e: PointerEvent<HTMLElement>) => {
    if (pointerIdRef.current !== e.pointerId) return;
    endDrag();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    let next: number;
    switch (e.key) {
      case 'ArrowLeft':
        next = width + KEY_STEP;
        break;
      case 'ArrowRight':
        next = width - KEY_STEP;
        break;
      case 'Home':
        next = bounds.min;
        break;
      case 'End':
        next = bounds.max;
        break;
      default:
        return;
    }
    e.preventDefault();
    setWidth(next, true);
  };

  return (
    <Drawer
      anchor="right"
      variant="temporary"
      open={open}
      onClose={onClose}
      PaperProps={{
        role: 'dialog',
        'aria-labelledby': titleId,
        sx: { width, maxWidth: '100%', display: 'flex', flexDirection: 'column', overflow: 'visible' },
      }}
    >
      <Box
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize panel"
        aria-valuenow={width}
        aria-valuemin={bounds.min}
        aria-valuemax={bounds.max}
        tabIndex={0}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerEnd}
        onPointerCancel={onPointerEnd}
        onLostPointerCapture={onPointerEnd}
        onKeyDown={onKeyDown}
        sx={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: 0,
          width: 6,
          zIndex: 1,
          cursor: 'col-resize',
          touchAction: 'none',
          outline: 'none',
          '&:hover, &:focus-visible, &:active': { bgcolor: 'primary.main' },
        }}
      />
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 3, py: 1.5, borderBottom: 1, borderColor: 'divider' }}
      >
        <Typography id={titleId} variant="h6" component="h2" sx={{ flex: 1, minWidth: 0, wordBreak: 'break-word' }}>
          {title}
        </Typography>
        <IconButton aria-label="Close" onClick={onClose}>
          <Close />
        </IconButton>
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', px: 3, py: 2 }}>{children}</Box>
    </Drawer>
  );
};
